/**
 * Módulo de Escaneo por Cámara (scanner.js)
 * Integra zxing-wasm para PDF417 y Tesseract.js para OCR de forma paralela.
 * Cumple con .idx-rules.md: modular, archivos < 300 líneas y funciones < 40 líneas.
 */

import { readBarcodes } from 'zxing-wasm';
import { createWorker } from 'tesseract.js';
import { parsePDF417, parseMRZ } from '../services/parser.js';
import { validateScan } from '../services/validator.js';
import { getCurrentUser } from './router.js';
import { cropVideoToMRZ, resizeCanvasIfNeeded } from '../services/imageProcessor.js';
import {
  toggleFlashlight as toggleFlashlightUI,
  renderScanResultOverlay,
  showScanAlert,
  updateFlashButtonState,
  checkFlashlightCapability,
  initCameraUIHandlers as initCameraUIHandlersUI
} from './scannerUI.js';

let activeStream = null;
let ocrInterval = null;
let ocrWorker = null;
let flashState = false;
let isProcessingScan = false;

window.addEventListener('start_camera_scan', () => startCamera());
window.addEventListener('stop_camera_scan', () => stopCamera());

/**
 * Detiene la cámara y libera los recursos.
 */
export function stopCamera() {
  if (activeStream) {
    activeStream.getTracks().forEach(track => track.stop());
    activeStream = null;
  }
  if (ocrInterval) {
    clearInterval(ocrInterval);
    ocrInterval = null;
  }
  isProcessingScan = false;
  flashState = false;
  updateFlashButtonState(false);
}

/**
 * Dibuja el frame actual de video en un canvas temporal y extrae ImageData.
 */
function getVideoFrameData(videoEl) {
  if (!videoEl || !videoEl.videoWidth || !videoEl.videoHeight) return null;
  const canvas = document.createElement('canvas');
  canvas.width = videoEl.videoWidth;
  canvas.height = videoEl.videoHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(videoEl, 0, 0);
  return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

/**
 * Bucle asíncrono recursivo para escaneo continuo de código de barras.
 */
async function scanFrameLoop(videoEl) {
  if (!activeStream || isProcessingScan) return;
  const imageData = getVideoFrameData(videoEl);
  if (imageData) {
    try {
      const results = await readBarcodes(imageData, {
        formats: ['PDF417'],
        tryHarder: true,
        tryRotate: true,
        tryDenoise: true,
        tryDownscale: true
      });
      if (results && results.length > 0 && !isProcessingScan) {
        const res = results[0];
        const parsed = parsePDF417(res.text, res.bytes) || parseMRZ(res.text);
        if (parsed) {
          await processScanResult(parsed, 'ZXING_CAM');
        }
      }
    } catch (err) {
      console.warn('Error en escaneo en tiempo real:', err);
    }
  }
  if (activeStream) {
    setTimeout(() => scanFrameLoop(videoEl), 250);
  }
}

/**
 * Inicia el stream de la cámara y arranca decodificación de código de barras y OCR.
 */
export async function startCamera() {
  stopCamera();
  const videoEl = document.getElementById('scanner-video');
  if (!videoEl) return;

  try {
    const constraints = {
      video: {
        facingMode: 'environment',
        width: { ideal: 1920, min: 1280 },
        height: { ideal: 1080, min: 720 }
      }
    };
    activeStream = await navigator.mediaDevices.getUserMedia(constraints);
    videoEl.srcObject = activeStream;
    videoEl.setAttribute('playsinline', true);

    scanFrameLoop(videoEl);
    startOCRProcessing(videoEl);
    checkFlashlightCapability(activeStream);
  } catch (error) {
    console.error('Error al iniciar la cámara:', error);
    showScanAlert('No se pudo acceder a la cámara. Use el simulador.', false);
  }
}

/**
 * Inicializa el worker de Tesseract con la whitelist MRZ.
 */
async function getOcrWorker() {
  if (!ocrWorker) {
    ocrWorker = await createWorker('eng');
    await ocrWorker.setParameters({
      tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<',
      tessedit_pageseg_mode: '6'
    });
  }
  return ocrWorker;
}

/**
 * Procesa cuadros de video cada 2s buscando zona MRZ en recorte inferior.
 */
async function startOCRProcessing(videoEl) {
  try {
    await getOcrWorker();
    ocrInterval = setInterval(async () => {
      try {
        if (isProcessingScan) return;
        if (videoEl.paused || videoEl.ended || !videoEl.videoWidth || !videoEl.videoHeight) return;

        const ocrCanvas = cropVideoToMRZ(videoEl);
        const { data: { text } } = await ocrWorker.recognize(ocrCanvas);
        const parsed = parseMRZ(text);
        if (parsed && !isProcessingScan) {
          await processScanResult(parsed, 'OCR_CAM');
        }
      } catch (ocrErr) {
        console.warn('Error en procesamiento OCR (ignorado):', ocrErr);
      }
    }, 2000);
  } catch (err) {
    console.error('Error en OCR Worker:', err);
  }
}

/**
 * Procesa y valida los datos estructurados del DNI.
 */
export async function processScanResult(parsedData, type) {
  if (isProcessingScan) return;
  isProcessingScan = true;

  const user = getCurrentUser();
  const porteroName = user ? user.username : 'Portero';

  try {
    const result = await validateScan(parsedData, type, porteroName);
    renderScanResultOverlay(result);
    window.dispatchEvent(new Event('capacity_changed'));
  } finally {
    setTimeout(() => { isProcessingScan = false; }, 4000);
  }
}

/**
 * Controla el encendido/apagado físico de la linterna.
 */
export async function toggleFlashlight() {
  flashState = await toggleFlashlightUI(activeStream, flashState);
}

/**
 * Intenta decodificar un canvas con zxing-wasm optimizado para PDF417.
 */
async function decodeCanvasWithZXing(canvas) {
  try {
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const results = await readBarcodes(imageData, {
      formats: ['PDF417'],
      tryHarder: true,
      tryRotate: true,
      tryDenoise: true,
      tryDownscale: true
    });
    if (results && results.length > 0) {
      return { text: results[0].text, rawBytes: results[0].bytes };
    }
  } catch (err) {
    console.warn('Error decoding canvas with zxing-wasm:', err);
  }
  return null;
}

/**
 * Genera un canvas a partir del video y lo redimensiona si es necesario.
 */
function getVideoCanvas(videoEl) {
  const canvas = document.createElement('canvas');
  canvas.width = videoEl.videoWidth;
  canvas.height = videoEl.videoHeight;
  canvas.getContext('2d').drawImage(videoEl, 0, 0);
  return resizeCanvasIfNeeded(canvas, 1600);
}

/**
 * Decodifica un canvas secuencialmente usando zxing-wasm y Tesseract OCR.
 */
async function decodeCanvas(canvas) {
  try {
    const decodeRes = await decodeCanvasWithZXing(canvas);
    if (decodeRes) {
      const parsed = parsePDF417(decodeRes.text, decodeRes.rawBytes) || parseMRZ(decodeRes.text);
      if (parsed) return parsed;
    }
  } catch (zxErr) {
    console.log('ZXing no detectó código en canvas:', zxErr);
  }
  try {
    const worker = await getOcrWorker();
    const ocrCanvas = resizeCanvasIfNeeded(canvas, 1600);
    const { data: { text } } = await worker.recognize(ocrCanvas);
    return parseMRZ(text);
  } catch (ocrErr) {
    console.error('Error en OCR de canvas:', ocrErr);
  }
  return null;
}

/**
 * Carga un archivo de imagen en un canvas original.
 */
async function loadImageToCanvas(file) {
  const img = new Image();
  img.src = URL.createObjectURL(file);
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
  });
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  canvas.getContext('2d').drawImage(img, 0, 0);
  URL.revokeObjectURL(img.src);
  return canvas;
}

/**
 * Captura el fotograma actual y ejecuta análisis de código y OCR.
 */
export async function captureAndDecode() {
  const videoEl = document.getElementById('scanner-video');
  if (!videoEl || !videoEl.videoWidth) {
    Swal.fire({ icon: 'warning', title: 'Cámara inactiva', text: 'La cámara no está activa.', confirmButtonText: 'Entendido' });
    return;
  }

  Swal.fire({ title: 'Procesando captura...', text: 'Analizando fotograma...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

  const processedCanvas = getVideoCanvas(videoEl);
  const parsed = await decodeCanvas(processedCanvas);

  Swal.close();
  if (parsed) {
    await processScanResult(parsed, 'CAPTURA_CAM');
  } else {
    Swal.fire({ icon: 'error', title: 'Lectura fallida', text: 'No se detectó un código legible. Intente alinear mejor el documento.', confirmButtonText: 'Entendido' });
  }
}

/**
 * Procesa la foto tomada con la cámara nativa del sistema o cargada desde un archivo.
 */
export async function handleSystemCameraUpload(event) {
  const file = event.target.files[0];
  if (!file) return;

  Swal.fire({ title: 'Procesando foto...', text: 'Cargando y analizando imagen...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

  try {
    const processedCanvas = await loadImageToCanvas(file);
    const parsed = await decodeCanvas(processedCanvas);

    Swal.close();
    event.target.value = '';

    if (parsed) {
      await processScanResult(parsed, 'FOTO_SUBIDA');
    } else {
      Swal.fire({ icon: 'error', title: 'Lectura fallida', text: 'No se detectó un código legible. Asegúrese de que la foto esté nítida y bien iluminada.', confirmButtonText: 'Entendido' });
    }
  } catch (err) {
    Swal.close();
    event.target.value = '';
    console.error('Error al procesar foto subida:', err);
    Swal.fire({ icon: 'error', title: 'Error de lectura', text: 'Ocurrió un error al cargar o analizar la imagen seleccionada.', confirmButtonText: 'Aceptar' });
  }
}

/**
 * Registra los listeners de los botones de captura y carga en el DOM.
 */
export function initCameraUIHandlers() {
  initCameraUIHandlersUI(
    () => captureAndDecode(),
    (e) => handleSystemCameraUpload(e),
    () => {
      const overlay = document.getElementById('scan-result-overlay');
      if (overlay) overlay.classList.remove('active');
      isProcessingScan = false;
    }
  );
}
